import ReconnectingWebSocket from 'reconnecting-websocket';
import { wait } from 'kineticUtils/functions/wait';

import { OnTrackHandler, OvenMediaMessage, SignalingConnectionProps, WowzaMessage } from './types';

class SignalingConnection {
  connection: ReconnectingWebSocket | undefined | null;
  onTrack: OnTrackHandler;
  onDisconnected: () => void;
  onError: (error?: any) => void;
  url: string;
  applicationName: string;
  streamName: string;
  peerConnection: RTCPeerConnection | undefined | null;
  sessionId = '[empty]';
  wowza: boolean;

  constructor({
    url,
    applicationName,
    streamName,
    wowza,
    onTrack,
    onDisconnected,
    onError,
  }: SignalingConnectionProps) {
    this.url = url;
    this.applicationName = applicationName;
    this.streamName = streamName;
    this.wowza = wowza;
    this.onTrack = onTrack;
    this.connect();
    this.onDisconnected = onDisconnected;
    this.onError = onError;
  }

  handleOpen = () => {
    this.peerConnection = new RTCPeerConnection({
      iceServers: [
        {
          urls: 'stun:stun.l.google.com:19302',
        },
      ],
    });

    this.peerConnection.oniceconnectionstatechange = async () => {
      if (this.peerConnection?.iceConnectionState === 'disconnected') {
        this.onDisconnected();
        const timeout = await wait(500);
        timeout && clearTimeout(timeout);
        // Retry the connection
        this.close();
        this.connect();
      }
    };

    this.peerConnection.ontrack = this.onTrack;
    this.sendGetOffer();
  };

  handleOnError = (error?: any) => {
    this.onError(error);
    // console.error(error);
  };

  connect = async () => {
    if (!this.url || (!this.url.startsWith('ws') && !this.url.startsWith('wss'))) {
      this.handleOnError(`Could not create the connection. Invalid websocket url: ${this.url}`);
      return;
    }
    this.connection = new ReconnectingWebSocket(this.url, [], {
      minReconnectionDelay: 1000,
    });

    this.connection.onerror = this.onError;

    this.connection.onopen = this.handleOpen;

    this.connection.onmessage = async (event) => {
      try {
        const message = JSON.parse(event.data);
        if (
          !message ||
          message.status === 514 ||
          message.code === 514 ||
          (message.status && message.status !== 200) ||
          (message.code && message.code !== 200)
        ) {
          if (message.status === 502) {
            return this.handleOnError(message);
          }
          const timeout = await wait(500);
          timeout && clearTimeout(timeout);
          await this.sendGetOffer();
          return;
        }

        this.sessionId = message?.streamInfo?.sessionId;

        if (!this.peerConnection) {
          return;
        }
        if (message?.sdp) {
          const sessionDesc = new RTCSessionDescription(message?.sdp);
          await this.peerConnection.setRemoteDescription(sessionDesc);
          const sessionDescInit = await this.peerConnection.createAnswer();
          await this.peerConnection.setLocalDescription(sessionDescInit);
          if (this.wowza) {
            this.sendPlay(sessionDescInit);
          } else {
            if (!this.peerConnection.localDescription) {
              this.handleOnError('sdp not found');
              return;
            }
            this?.sendPlay(message);
          }
        }
        const iceCandidates = message.iceCandidates || message.candidates;
        if (iceCandidates !== undefined) {
          for (const index in iceCandidates) {
            this.peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]));
          }
        }
        if (message.command) {
          if ('sendResponse'.localeCompare(message.command) === 0) {
            if (this.connection) {
              this.connection.close();
            }
            this.connection = null;
          }
        }
      } catch (e) {
        this.handleOnError(e);
      }
    };
  };

  sendGetOffer = async () => {
    if (this.wowza) {
      // TODO any user data
      const userData = {};
      const data: WowzaMessage = {
        streamInfo: {
          applicationName: this.applicationName,
          sessionId: this.sessionId,
          streamName: this.streamName,
          url: this.url,
          wowza: this.wowza,
        },
        userData,
        direction: 'play',
        command: 'getOffer',
      };
      await this.connection?.send(JSON.stringify(data));
    } else {
      const data: OvenMediaMessage = {
        command: 'request_offer',
      };
      await this.connection?.send(JSON.stringify(data));
    }
  };

  // TODO what is msg type
  sendPlay = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    msg?: any
  ) => {
    if (!this?.peerConnection) {
      this.handleOnError('trying to send play with no peer connection');
      return;
    }
    if (this.wowza) {
      // any user data
      const userData = {};
      const data: WowzaMessage = {
        streamInfo: {
          applicationName: this.applicationName,
          sessionId: this.sessionId,
          streamName: this.streamName,
          url: this.url,
          wowza: this.wowza,
        },
        userData,
        sdp: this.peerConnection.localDescription as RTCSessionDescriptionInit,
        command: 'sendResponse',
        direction: 'play',
      };
      this.connection?.send(JSON.stringify(data));
    } else {
      const data: OvenMediaMessage = {
        sdp: this.peerConnection.localDescription as RTCSessionDescription,
        command: 'answer',
        id: msg.id,
        peer_id: msg.peer_id,
      };
      this.connection?.send(JSON.stringify(data));
    }
  };

  close = async () => {
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    this.peerConnection = null;
    if (this.connection) {
      this.connection.close();
      this.connection.onerror = null;
    }
    this.connection = null;
  };
}

export default SignalingConnection;
